import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useWindowSize } from '@/hooks/useWindowSize';
import { Button, MoneyField } from '@scramble/ui';
import { prettyMoney } from '@scramble/utils';
import { prettyDate } from '@/helpers/utils';
import { DATE_FORMAT_D_MMMM_YYYY } from '@/helpers/common';
import { useEarlyReservation } from '@/hooks/shared/useEarlyReservation';
import IconInfo from "@scramble/design-system/icons/info-circle.svg";
import s from './index.module.scss';

const id = MODAL_IDS.early_reservation_modal;

const VALIDATION_SCHEMA = (t, max) =>
  Yup.object().shape({
    amount: Yup.number()
      .max(
        max,
        t('modal:early_reservation_modal.validation_max', { maxAmount: max }),
      )
      .required(t('modal:early_reservation_modal.enter_reservation')),
  });

export const EarlyReservationModal = (props) => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

export const Modal = () => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const { limits, status, reservedAmount, reserve } = useEarlyReservation();

  const { closeModal } = useModal();

  const onSubmit = async (values, formikBag) => {
    const res = await reserve({ amount: values.amount });
    if (res?.success) {
      formikBag.setFieldValue('isSubmitted', true);
      closeModal(id);
      return;
    }

    if (res?.code) {
      formikBag.setFieldError('amount', res.message);
    }
  };

  const { values, setFieldValue, handleSubmit, errors, touched } = useFormik({
    onSubmit,
    enableReinitialize: true,
    validationSchema: VALIDATION_SCHEMA(t, limits?.personal_limit || 0),
    initialValues: {
      amount: reservedAmount || 0,
    },
  });

  const content = (
    <form className={s.modal} onSubmit={handleSubmit}>
      <div className={s.header}>
        <h1 className={s.title}>{t('modal:early_reservation_modal.title')}</h1>
      </div>
      <div className={s.content}>
        <div className={s.content_inner}>
          <div>
            <div className={s.row}>
              <div className="f-body-md">
                {t('modal:early_reservation_modal.your_limit')}
              </div>
              <div className="f-body-md f-500">
                {prettyMoney(limits?.personal_limit)}
              </div>
            </div>
            <div className={s.row}>
              <div className="f-body-md">
                {t('modal:early_reservation_modal.available_limit')}
              </div>
              <div className="f-body-md f-500">
                {prettyMoney(limits?.available_reserve)}
              </div>
            </div>
          </div>
          <MoneyField
            name="amount"
            label={t('modal:early_reservation_modal.reserve_amount')}
            placeholder={prettyMoney(0)}
            value={values.amount}
            error={touched.amount && errors.amount}
            onChange={amount => setFieldValue('amount', amount)}
            onClear={() => setFieldValue('amount', '0')}
          />
          <div className={s.note}>
            <img src={IconInfo} alt="info" width={20} height={20} />
            <span className="f-body-sm">
              {t('modal:early_reservation_modal.reserve_must_used', {
                time: '23:59',
                date: prettyDate(status?.reservation_expires_at, DATE_FORMAT_D_MMMM_YYYY)
              })}
            </span>
          </div>
        </div>
      </div>
      <div className={s.footer}>
        <Button
          theme="neutral"
          appearance="tonal"
          size="lg"
          label={t('modal:early_reservation_modal.cancel')}
          onClick={() => closeModal(id)}
        />
        <Button
          theme="neutral"
          appearance="filled"
          size="lg"
          type="submit"
          label={t('modal:early_reservation_modal.reserve')}
        />
      </div>
    </form>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};